import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import { LG_NAMES } from "@mhj/shared/wards";

type EmpowermentSettings = {
  allowedLgas: string[];
};

export default function AdminEmpowermentSettings() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const [selectedLgas, setSelectedLgas] = useState<string[]>([]);

  const { data, isLoading } = useQuery<EmpowermentSettings>({
    queryKey: ["admin", "empowerment-settings"],
    queryFn: () => apiRequest<EmpowermentSettings>("/admin/empowerment-settings"),
  });

  useEffect(() => {
    if (data) setSelectedLgas(data.allowedLgas);
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (payload: EmpowermentSettings) =>
      apiRequest<EmpowermentSettings>("/admin/empowerment-settings", {
        method: "PUT",
        data: payload,
        withCsrf: true,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["admin", "empowerment-settings"], updated);
      toast.success("Settings saved.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleToggleLga = (lga: string) => {
    setSelectedLgas((prev) =>
      prev.includes(lga) ? prev.filter((l) => l !== lga) : [...prev, lga],
    );
  };

  const handleSave = () => {
    if (selectedLgas.length === 0) {
      toast.error("Select at least one LGA.");
      return;
    }
    updateMutation.mutate({ allowedLgas: selectedLgas });
  };

  if (isLoading && !data) {
    return (
      <div className="glass-card rounded-3xl p-6">
        <p className="text-sm text-slate-500">Loading settings…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <h2 className="text-lg font-semibold text-slate-900">
          Empowerment application settings
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Applicants always see every Niger State LGA in the form — this
          controls which of them the application actually accepts. Nothing on
          the public site or the portal states this restriction; an applicant
          outside the allowed set finds out at the point of applying.
        </p>

        {!isSuperAdmin && (
          <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You can view the allowed LGAs. Changing them is restricted to a
            super admin.
          </p>
        )}

        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Allowed LGAs ({selectedLgas.length})
            </p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {LG_NAMES.map((lga) => (
              <label
                key={lga}
                className={`flex items-center gap-2 rounded-lg border border-sky-100 bg-white px-3 py-2 transition ${
                  isSuperAdmin ? "cursor-pointer hover:border-sky-200" : "opacity-60"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedLgas.includes(lga)}
                  onChange={() => handleToggleLga(lga)}
                  disabled={!isSuperAdmin}
                  className="h-3.5 w-3.5 rounded border-sky-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-slate-700">{lga}</span>
              </label>
            ))}
          </div>
        </div>

        {isSuperAdmin && (
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {updateMutation.isPending ? "Saving…" : "Save settings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
